/**
 * Slide cart personalisation.
 * One Personalisation value per unit of the configured product.
 * A quantity of 2 is saved as two lines when the texts differ.
 * Each personalised unit also adds the configured engraving variant.
 */
(function () {
  if (window.cartPersonalisationReady) {
    return;
  }

  window.cartPersonalisationReady = true;

  const PROP_MAIN = '_personalisation_main';
  const PROP_ENGRAVE_VARIANT = '_personalisation_engrave_variant';

  let isSaving = false;
  let isRemovingLinked = false;

  /**
   * Storefront root, including locale prefix.
   * @returns {string}
   */
  function getShopifyRoot() {
    let root = '/';

    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      root = Shopify.routes.root;
    }

    return root.replace(/\/$/, '') + '/';
  }

  /**
   * @param {string} url
   * @param {RequestInit} [options]
   * @returns {Promise<object>}
   */
  async function fetchJson(url, options) {
    const response = await fetch(url, options);
    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message = (data && (data.description || data.message))
        || 'Unable to save personalisation. Please try again.';

      throw new Error(message);
    }

    return data || {};
  }

  /**
   * @param {HTMLElement} root
   * @param {string} [message]
   */
  function showError(root, message) {
    const errorEl = root.querySelector('[data-cart-pers-error]');
    const input = root.querySelector('[data-cart-pers-input]');
    const text = message || '';

    if (errorEl) {
      errorEl.textContent = text;
      errorEl.hidden = text === '';
    }

    if (!input) {
      return;
    }

    if (text === '') {
      input.classList.remove('is-invalid');
      input.removeAttribute('aria-invalid');
      return;
    }

    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
  }

  /**
   * @param {HTMLElement} root
   */
  function updateCount(root) {
    const input = root.querySelector('[data-cart-pers-input]');
    const current = root.querySelector('[data-cart-pers-count-current]');

    if (!input || !current) {
      return;
    }

    current.textContent = String(input.value.length);
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} open
   * @param {boolean} [focusInput]
   */
  function setFieldsOpen(root, open, focusInput) {
    const fields = root.querySelector('[data-cart-pers-fields]');
    const toggle = root.querySelector('[data-cart-pers-toggle]');
    const input = root.querySelector('[data-cart-pers-input]');

    if (fields) {
      fields.hidden = !open;
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (!open) {
      showError(root, '');
      return;
    }

    if (focusInput && input) {
      input.focus();
    }
  }

  /**
   * Copy line properties. Blank value omits Personalisation.
   * A saved value also links the engraving variant.
   * @param {object} item
   * @param {string} propertyName
   * @param {string} value
   * @param {number} engraveVariantId
   * @returns {Object<string, string>}
   */
  function buildProperties(item, propertyName, value, engraveVariantId) {
    const properties = {};
    const source = item.properties || {};
    const linkKeys = [PROP_MAIN, PROP_ENGRAVE_VARIANT];

    Object.keys(source).forEach(function (key) {
      const propValue = source[key];

      if (key === propertyName || linkKeys.indexOf(key) !== -1) {
        return;
      }

      if (propValue == null || String(propValue).trim() === '') {
        return;
      }

      properties[key] = String(propValue);
    });

    if (value) {
      properties[propertyName] = value;

      if (engraveVariantId) {
        properties[PROP_MAIN] = 'true';
        properties[PROP_ENGRAVE_VARIANT] = String(engraveVariantId);
      }
    }

    return properties;
  }

  /**
   * @param {object} item
   * @returns {number|string|null}
   */
  function sellingPlanId(item) {
    const allocation = item.selling_plan_allocation;
    const plan = allocation && allocation.selling_plan;

    if (!plan || !plan.id) {
      return null;
    }

    return plan.id;
  }

  /**
   * @param {string} lineKey
   * @returns {HTMLElement[]}
   */
  function slotsForLine(lineKey) {
    const slots = [];

    document.querySelectorAll('[data-cart-pers]').forEach(function (slot) {
      if (slot.getAttribute('data-line-key') === lineKey) {
        slots.push(slot);
      }
    });

    return slots;
  }

  /**
   * @param {HTMLElement} slot
   * @returns {{ slot: HTMLElement, checked: boolean, text: string, value: string }}
   */
  function readSlot(slot) {
    const toggle = slot.querySelector('[data-cart-pers-toggle]');
    const input = slot.querySelector('[data-cart-pers-input]');
    const checked = Boolean(toggle && toggle.checked);
    const text = input ? input.value.trim() : '';

    return {
      slot: slot,
      checked: checked,
      text: text,
      value: checked ? text : '',
    };
  }

  /**
   * @param {string[]} values
   * @returns {Map<string, number>}
   */
  function tallyValues(values) {
    const counts = new Map();

    values.forEach(function (value) {
      const current = counts.get(value) || 0;

      counts.set(value, current + 1);
    });

    return counts;
  }

  /**
   * Keep the current line's text when it is still wanted.
   * @param {string} existing
   * @param {Map<string, number>} counts
   * @returns {string}
   */
  function chooseKeepValue(existing, counts) {
    if (counts.has(existing)) {
      return existing;
    }

    const values = Array.from(counts.keys());
    let index = 0;

    while (index < values.length) {
      if (values[index] !== '') {
        return values[index];
      }

      index += 1;
    }

    return '';
  }

  /**
   * @param {string} url
   * @param {object} payload
   * @returns {Promise<object>}
   */
  function postJson(url, payload) {
    return fetchJson(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
  }

  /**
   * @param {HTMLElement[]} slots
   * @param {boolean} busy
   */
  function setSlotsBusy(slots, busy) {
    slots.forEach(function (slot) {
      const button = slot.querySelector('[data-cart-pers-add]');

      if (!button) {
        return;
      }

      button.disabled = busy;

      if (busy) {
        button.setAttribute('aria-busy', 'true');
        return;
      }

      button.removeAttribute('aria-busy');
    });
  }

  /**
   * @param {object} item
   * @param {string} propertyName
   * @returns {string}
   */
  function existingValue(item, propertyName) {
    const props = item.properties || {};
    const value = props[propertyName];

    if (value == null) {
      return '';
    }

    return String(value).trim();
  }

  /**
   * @param {HTMLElement} root
   */
  async function savePersonalisation(root) {
    const input = root.querySelector('[data-cart-pers-input]');
    const lineKey = root.getAttribute('data-line-key');
    const propertyName = root.getAttribute('data-property-name')
      || 'Personalisation';
    const clicked = readSlot(root);
    const engraveVariantId = parseInt(
      root.getAttribute('data-engrave-variant-id'),
      10,
    ) || 0;
    let slots = [];
    let didMutate = false;

    if (isSaving) {
      return;
    }

    if (!lineKey) {
      showError(root, root.getAttribute('data-error-missing'));
      return;
    }

    if (clicked.checked && clicked.text === '') {
      showError(root, root.getAttribute('data-error-empty'));

      if (input) {
        input.focus();
      }

      return;
    }

    slots = slotsForLine(lineKey);
    isSaving = true;
    setSlotsBusy(slots, true);
    slots.forEach(function (slot) {
      showError(slot, '');
    });

    try {
      const cart = await fetchJson(getShopifyRoot() + 'cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });
      const items = cart.items || [];
      let item = null;
      let values = [];
      let reads = [];
      let invalid = false;

      items.forEach(function (line) {
        if (line.key === lineKey) {
          item = line;
        }
      });

      if (!item) {
        showError(root, root.getAttribute('data-error-missing'));
        return;
      }

      if (slots.length === item.quantity) {
        reads = slots.map(readSlot);

        reads.forEach(function (read) {
          if (!read.checked || read.text !== '') {
            return;
          }

          invalid = true;
          showError(read.slot, read.slot.getAttribute('data-error-empty'));
        });

        if (invalid) {
          return;
        }

        values = reads.map(function (read) {
          return read.value;
        });
      } else {
        const shared = existingValue(item, propertyName);
        let unit = 1;

        values = [clicked.value];

        while (unit < item.quantity) {
          values.push(shared);
          unit += 1;
        }
      }

      const existing = existingValue(item, propertyName);
      const unchanged = values.every(function (value) {
        return value === existing;
      });

      if (unchanged) {
        return;
      }

      const counts = tallyValues(values);
      const keepValue = chooseKeepValue(existing, counts);
      const keepCount = counts.get(keepValue) || 0;
      const sellingPlan = sellingPlanId(item);
      const linkedEngrave = engraveVariantId
        && engraveVariantId !== item.variant_id
        ? engraveVariantId
        : 0;
      const changePayload = {
        id: item.key,
        quantity: keepCount,
        properties: buildProperties(
          item,
          propertyName,
          keepValue,
          linkedEngrave,
        ),
      };
      const toAdd = [];
      let engraveQty = 0;

      if (!keepCount) {
        throw new Error(root.getAttribute('data-error-save'));
      }

      if (sellingPlan) {
        changePayload.selling_plan = sellingPlan;
      }

      counts.forEach(function (count, text) {
        const payload = {
          id: item.variant_id,
          quantity: count,
          properties: buildProperties(
            item,
            propertyName,
            text,
            linkedEngrave,
          ),
        };

        if (text) {
          engraveQty += count;
        }

        if (text === keepValue) {
          return;
        }

        if (sellingPlan) {
          payload.selling_plan = sellingPlan;
        }

        toAdd.push(payload);
      });

      if (linkedEngrave && engraveQty > 0) {
        toAdd.push({
          id: linkedEngrave,
          quantity: engraveQty,
        });
      }

      await postJson(getShopifyRoot() + 'cart/change.js', changePayload);
      didMutate = true;

      if (toAdd.length) {
        await postJson(getShopifyRoot() + 'cart/add.js', { items: toAdd });
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true }),
      );
    } catch (error) {
      const fallback = root.getAttribute('data-error-save');

      showError(root, (error && error.message) || fallback);

      if (didMutate) {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true }),
        );
      }
    } finally {
      isSaving = false;
      setSlotsBusy(slots, false);
    }
  }

  document.addEventListener('change', function (event) {
    const toggle = event.target.closest('[data-cart-pers-toggle]');

    if (!toggle) {
      return;
    }

    const root = toggle.closest('[data-cart-pers]');

    if (!root) {
      return;
    }

    setFieldsOpen(root, toggle.checked, true);
  });

  document.addEventListener('input', function (event) {
    const input = event.target.closest('[data-cart-pers-input]');

    if (!input) {
      return;
    }

    const root = input.closest('[data-cart-pers]');

    if (!root) {
      return;
    }

    updateCount(root);
    showError(root, '');
  });

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-cart-pers-add]');

    if (!button) {
      return;
    }

    const root = button.closest('[data-cart-pers]');

    if (!root) {
      return;
    }

    savePersonalisation(root);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') {
      return;
    }

    const input = event.target.closest
      ? event.target.closest('[data-cart-pers-input]')
      : null;

    if (!input) {
      return;
    }

    event.preventDefault();

    const root = input.closest('[data-cart-pers]');

    if (!root) {
      return;
    }

    savePersonalisation(root);
  }, true);

  /**
   * @param {object} item
   * @param {number} engraveVariantId
   * @returns {boolean}
   */
  function isEngraveLine(item, engraveVariantId) {
    if (!item || !engraveVariantId) {
      return false;
    }

    return parseInt(item.variant_id, 10) === engraveVariantId
      || parseInt(item.id, 10) === engraveVariantId;
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<object>}
   */
  function changeLineQuantity(key, quantity) {
    return postJson(getShopifyRoot() + 'cart/change.js', {
      id: key,
      quantity: quantity,
    });
  }

  /**
   * Remove button, or the minus button when quantity is already at the minimum.
   * @param {EventTarget|null} target
   * @returns {string|null}
   */
  function resolveCartLineKey(target) {
    if (!target || !target.closest) {
      return null;
    }

    const removeBtn = target.closest('[data-cart-remove]');

    if (removeBtn) {
      return removeBtn.getAttribute('data-id');
    }

    const decreaseBtn = target.closest('[data-decrease-qty]');

    if (!decreaseBtn) {
      return null;
    }

    const wrapper = decreaseBtn.closest('[data-quantity-wrapper]');
    const qtyInput = wrapper
      ? wrapper.querySelector('[data-quantity-value]')
      : null;

    if (!qtyInput) {
      return null;
    }

    const qty = parseInt(qtyInput.value, 10) || 0;
    const minQty = parseInt(qtyInput.getAttribute('min'), 10) || 1;

    if (qty > minQty) {
      return null;
    }

    return qtyInput.getAttribute('data-id');
  }

  /**
   * Removing a personalised line also removes its linked engraving units.
   * @param {string} lineKey
   * @param {HTMLElement} cartRow
   * @param {Event} event
   */
  async function handleLinkedRemoval(lineKey, cartRow, event) {
    let engraveVariantId = parseInt(
      cartRow.getAttribute('data-personalisation-engrave-variant'),
      10,
    ) || 0;
    let mainItem = null;
    const updates = [{ key: lineKey, quantity: 0 }];

    if (event.cancelable) {
      event.preventDefault();
    }

    event.stopPropagation();
    event.stopImmediatePropagation();
    isRemovingLinked = true;

    try {
      const cart = await fetchJson(getShopifyRoot() + 'cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });
      const items = cart.items || [];

      items.forEach(function (line) {
        if (line.key === lineKey) {
          mainItem = line;
        }
      });

      if (
        mainItem
        && mainItem.properties
        && mainItem.properties[PROP_ENGRAVE_VARIANT]
      ) {
        engraveVariantId = parseInt(
          mainItem.properties[PROP_ENGRAVE_VARIANT],
          10,
        ) || engraveVariantId;
      }

      if (engraveVariantId) {
        let index = 0;

        while (index < items.length) {
          const item = items[index];

          index += 1;

          if (item.key === lineKey || !isEngraveLine(item, engraveVariantId)) {
            continue;
          }

          const remaining = item.quantity - (mainItem ? mainItem.quantity : 1);

          updates.push({
            key: item.key,
            quantity: remaining > 0 ? remaining : 0,
          });
          break;
        }
      }

      let step = 0;

      while (step < updates.length) {
        await changeLineQuantity(updates[step].key, updates[step].quantity);
        step += 1;
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true }),
      );
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true }),
      );
    } finally {
      isRemovingLinked = false;
    }
  }

  document.addEventListener('click', function (event) {
    const lineKey = resolveCartLineKey(event.target);
    const cartRow = event.target.closest
      ? event.target.closest('[data-cart-item]')
      : null;

    if (!lineKey || !cartRow || isRemovingLinked || isSaving) {
      return;
    }

    if (cartRow.getAttribute('data-personalisation-role') !== 'main') {
      return;
    }

    handleLinkedRemoval(lineKey, cartRow, event);
  }, true);
})();
